import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import Icon from '../../common/Icons';
import { formatNumber } from '../ui';
import { severityLabel, stockStatusLabel } from '../pharmacyCopy';
import {
  computeLineTotal,
  formatPharmacyLineTotal,
  formatPharmacyPrice
} from '../utils/dispensationPricing';
import { severityPillClass } from './dispensationUi';
import { searchMedications } from './medicationSearch';
import { HighlightedText, useSmartPickerControls } from './smartPickerControls';

const POSOLOGY_SNIPPETS = ['1 cp x3/j', '1 cp matin et soir', '1 cp le soir', 'pendant 7 jours'];

function stockPillClass(status) {
  if (status === 'OUT_OF_STOCK') return 'cp-status-pill--danger';
  if (status === 'LOW_STOCK') return 'cp-status-pill--warn';
  return 'cp-status-pill--ok';
}

function lineKey(line, idx) {
  return line.medication_id ? `m-${line.medication_id}` : `i-${idx}`;
}

export default function SmartDispensationComposer({
  medications = [],
  siteId,
  siteLabel = '',
  value,
  onChange,
  copy,
  interactionPreview = null
}) {
  const [stockByMed, setStockByMed] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef(null);
  const listRef = useRef(null);

  const selectedIds = useMemo(
    () => new Set(value.items.map((l) => String(l.medication_id)).filter(Boolean)),
    [value.items]
  );

  const medById = useMemo(() => {
    const map = {};
    medications.forEach((m) => {
      map[String(m.id)] = m;
    });
    return map;
  }, [medications]);

  useEffect(() => {
    if (!siteId) {
      setStockByMed({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setStockLoading(true);
      try {
        const res = await pharmacyService.getSiteStock(siteId, { site_inventory_only: 'true' });
        if (cancelled) return;
        const map = {};
        (res.success ? res.data || [] : []).forEach((row) => {
          map[String(row.medication_id)] = row;
        });
        setStockByMed(map);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const patch = useCallback(
    (key, next) => onChange({ ...value, [key]: next }),
    [onChange, value]
  );

  const patchItems = useCallback(
    (items) => onChange({ ...value, items }),
    [onChange, value]
  );

  const pickerResults = useMemo(() => {
    const candidates = medications.filter((m) => !selectedIds.has(String(m.id)));
    return searchMedications(candidates, pickerQuery, { limit: 24 })
      .map((m) => ({ med: m, stock: stockByMed[String(m.id)] }))
      .filter(({ stock }) => stock && Number(stock.on_hand || 0) > 0)
      .slice(0, 12);
  }, [medications, pickerQuery, selectedIds, stockByMed]);

  const pickerResultsRef = useRef(pickerResults);
  pickerResultsRef.current = pickerResults;

  const addMedication = useCallback(
    (med) => {
      if (selectedIds.has(String(med.id))) return;
      const stock = stockByMed[String(med.id)];
      if (!stock || Number(stock.on_hand || 0) <= 0) return;
      const line = {
        medication_id: String(med.id),
        quantity: '1',
        dosage_instructions: ''
      };
      patchItems([line, ...value.items.filter((l) => l.medication_id)]);
      setPickerQuery('');
      return true;
    },
    [selectedIds, stockByMed, value.items, patchItems]
  );

  const addMedicationRef = useRef(addMedication);
  addMedicationRef.current = addMedication;

  const { open, setOpen, active, setActive, onKeyDown } = useSmartPickerControls({
    itemCount: pickerResults.length,
    disabled: !siteId,
    onPickIndex: (idx) => {
      const row = pickerResultsRef.current[idx];
      if (row) addMedicationRef.current(row.med);
    }
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [setOpen]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pickMedication = (med) => {
    if (addMedication(med)) setOpen(false);
  };

  const updateLine = (idx, patchLine) => {
    patchItems(value.items.map((line, i) => (i === idx ? { ...line, ...patchLine } : line)));
  };

  const removeLine = (idx) => {
    patchItems(value.items.filter((_, i) => i !== idx));
  };

  const validCount = value.items.filter((l) => l.medication_id && l.quantity).length;

  const grandTotal = useMemo(() => {
    return value.items.reduce((sum, line) => {
      if (!line.medication_id) return sum;
      const stock = stockByMed[String(line.medication_id)];
      const med = medById[String(line.medication_id)];
      const unitPrice = stock?.price ?? med?.price ?? null;
      const lineTotal = computeLineTotal(line.quantity, unitPrice);
      return sum + (lineTotal || 0);
    }, 0);
  }, [medById, stockByMed, value.items]);

  const pricedLineCount = useMemo(() => {
    return value.items.filter((line) => {
      if (!line.medication_id) return false;
      const stock = stockByMed[String(line.medication_id)];
      const med = medById[String(line.medication_id)];
      const unitPrice = stock?.price ?? med?.price ?? null;
      return computeLineTotal(line.quantity, unitPrice) != null;
    }).length;
  }, [medById, stockByMed, value.items]);

  return (
    <div className="cp-smart-order cp-dispense-composer">
      <section className="cp-form-section">
        <h3 className="cp-form-section__title">{copy.SECTION_HEADER}</h3>
        <div className="cp-form-grid">
          {siteLabel ? (
            <Form.Group className="cp-form-field">
              <Form.Label>{copy.FIELD_SITE}</Form.Label>
              <Form.Control plaintext readOnly className="form-control-plaintext" value={siteLabel} />
            </Form.Group>
          ) : null}
          <Form.Group className="cp-form-field">
            <Form.Label>{copy.FIELD_PATIENT}</Form.Label>
            <Form.Control
              placeholder={copy.FIELD_PATIENT_PLACEHOLDER}
              value={value.patient_name}
              onChange={(e) => patch('patient_name', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="cp-form-field cp-form-field--full">
            <Form.Label>{copy.FIELD_NOTES}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={value.notes}
              onChange={(e) => patch('notes', e.target.value)}
              placeholder={copy.FIELD_NOTES_PLACEHOLDER}
            />
          </Form.Group>
        </div>
      </section>

      <section className="cp-form-section cp-smart-order__lines">
        <div className="cp-form-section__head">
          <h3 className="cp-form-section__title mb-0">{copy.SECTION_LINES}</h3>
          <span className="cp-form-section__meta">
            {copy.LINES_COUNT.replace('{n}', String(validCount))}
          </span>
        </div>

        <div className="cp-smart-order__assist">
          <div className="cp-smart-order__assist-text">
            <Icon name="warehouse" size={15} aria-hidden />
            <span>
              {stockLoading
                ? copy.SMART_STOCK_LOADING
                : siteId
                  ? copy.SMART_STOCK_HINT
                  : copy.SMART_NO_SITE}
            </span>
          </div>
        </div>

        <div className="cp-smart-picker" ref={pickerRef}>
          <label className="cp-form-field mb-0 w-100">
            <span className="form-label">{copy.SMART_ADD_LABEL}</span>
            <div className="cp-smart-picker__control">
              <Icon name="search" size={16} className="cp-smart-picker__icon" aria-hidden />
              <input
                type="search"
                className="cp-smart-picker__input"
                placeholder={copy.SMART_ADD_PLACEHOLDER}
                value={pickerQuery}
                onChange={(e) => {
                  setPickerQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                disabled={!siteId}
                role="combobox"
                aria-expanded={open}
                aria-controls="cp-dispense-med-list"
                aria-autocomplete="list"
              />
            </div>
          </label>
          {open ? (
            <ul className="cp-smart-picker__list" id="cp-dispense-med-list" role="listbox" ref={listRef}>
              {pickerResults.length === 0 ? (
                <li className="cp-smart-picker__empty">{copy.SMART_NO_MATCH}</li>
              ) : (
                pickerResults.map(({ med, stock }, idx) => {
                  const isActive = idx === active;
                  return (
                    <li key={med.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive ? 'true' : undefined}
                        className={`cp-smart-picker__option${isActive ? ' is-active' : ''}`}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => pickMedication(med)}
                      >
                        <span className="cp-smart-picker__option-main">
                          <strong>
                            <HighlightedText text={med.name} query={pickerQuery} />
                          </strong>
                          <span className="cp-smart-picker__option-meta">
                            {med.unit_name || 'unités'} · stock {formatNumber(stock?.on_hand)}
                          </span>
                        </span>
                        <span className={`cp-status-pill ${stockPillClass(stock?.stock_status)}`}>
                          {stockStatusLabel(stock?.stock_status)}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </div>

        {value.items.filter((l) => l.medication_id).length === 0 ? (
          <div className="cp-smart-order__empty">
            <Icon name="pills" size={22} aria-hidden />
            <p className="mb-0">{copy.SMART_EMPTY_LINES}</p>
          </div>
        ) : (
          <div className="cp-table-wrap cp-smart-lines-table-wrap">
            <table className="cp-table cp-smart-lines-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{copy.COL_MED}</th>
                  <th className="text-end">{copy.COL_STOCK}</th>
                  <th className="text-end" style={{ minWidth: 90 }}>
                    {copy.COL_QTY}
                  </th>
                  <th style={{ minWidth: 160 }}>{copy.COL_DOSAGE}</th>
                  <th className="text-end">{copy.COL_PRICE}</th>
                  <th className="text-end">{copy.LINE_TOTAL}</th>
                  <th className="text-end" style={{ width: 52 }} />
                </tr>
              </thead>
              <tbody>
                {value.items
                  .map((line, idx) => ({ line, idx }))
                  .filter(({ line }) => line.medication_id)
                  .map(({ line, idx }, displayIdx, rows) => {
                    const med = medById[String(line.medication_id)];
                    const stock = stockByMed[String(line.medication_id)];
                    const name =
                      med?.name || stock?.medication_name || `Médicament #${line.medication_id}`;
                    const rowNum = rows.length - displayIdx;
                    const onHand = Number(stock?.on_hand || 0);
                    const qty = parseInt(line.quantity, 10) || 0;
                    const overStock = qty > onHand;
                    const unitPrice = stock?.price ?? med?.price ?? null;
                    const lineTotal = computeLineTotal(line.quantity, unitPrice);

                    return (
                      <tr
                        key={lineKey(line, idx)}
                        className={overStock ? 'cp-smart-lines-table__row--warn' : ''}
                      >
                        <td className="cp-num text-muted">{rowNum}</td>
                        <td>
                          <strong className="cp-smart-lines-table__name">{name}</strong>
                          <div className="cp-smart-lines-table__meta text-muted small">
                            {med?.unit_name || stock?.unit_name || '—'}
                          </div>
                          {overStock ? (
                            <div className="cp-smart-lines-table__warn small text-danger">
                              {copy.OVER_STOCK.replace('{n}', formatNumber(onHand))}
                            </div>
                          ) : null}
                        </td>
                        <td className="text-end">
                          {stock ? (
                            <span className={`cp-status-pill ${stockPillClass(stock.stock_status)}`}>
                              {formatNumber(stock.on_hand)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="text-end">
                          <Form.Control
                            type="number"
                            min={1}
                            max={onHand > 0 ? onHand : undefined}
                            size="sm"
                            className="cp-smart-lines-table__qty text-end"
                            value={line.quantity}
                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            size="sm"
                            list={`cp-dosage-snippets-${idx}`}
                            placeholder={copy.COL_DOSAGE_PLACEHOLDER}
                            value={line.dosage_instructions || ''}
                            onChange={(e) =>
                              updateLine(idx, { dosage_instructions: e.target.value })
                            }
                          />
                          <datalist id={`cp-dosage-snippets-${idx}`}>
                            {POSOLOGY_SNIPPETS.map((s) => (
                              <option key={s} value={s} />
                            ))}
                          </datalist>
                        </td>
                        <td className="text-end cp-num cp-num--muted">
                          {formatPharmacyPrice(unitPrice) || copy.NO_PRICE}
                        </td>
                        <td className="text-end cp-num cp-smart-line__subtotal">
                          <strong>{formatPharmacyLineTotal(lineTotal)}</strong>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="cp-btn cp-btn--danger cp-btn--sm"
                            onClick={() => removeLine(idx)}
                            aria-label={copy.SMART_REMOVE}
                          >
                            <Icon name="trash" size={13} aria-hidden />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              {validCount > 0 ? (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="text-end fw-bold">
                      {copy.TOTAL_PRICE}
                    </td>
                    <td className="text-end cp-num fw-bold">{formatPharmacyLineTotal(grandTotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        )}

        {validCount > 0 ? (
          <div className="cp-dispense-pricing-summary">
            <span>
              {copy.LINES_COUNT.replace('{n}', String(validCount))}
              {pricedLineCount < validCount ? ` · ${copy.PRICE_PARTIAL_HINT}` : ''}
            </span>
            <strong>{formatPharmacyLineTotal(grandTotal)}</strong>
          </div>
        ) : null}

        {interactionPreview && interactionPreview.interactions?.length > 0 ? (
          <div
            className={`cp-interaction-panel${interactionPreview.blocking ? ' cp-interaction-panel--danger' : ' cp-interaction-panel--warn'}`}
          >
            <div className="cp-interaction-panel__head">
              <Icon name="shield" size={16} aria-hidden />
              <strong>
                {copy.INTERACTION_TITLE.replace(
                  '{severity}',
                  severityLabel(interactionPreview.highestSeverity)
                )}
              </strong>
              <span className={`cp-status-pill ${severityPillClass(interactionPreview.highestSeverity)}`}>
                {severityLabel(interactionPreview.highestSeverity)}
              </span>
            </div>
            <ul className="cp-interaction-panel__list mb-0">
              {interactionPreview.interactions.map((i) => (
                <li key={i.id}>
                  <strong>{i.medication_a_name}</strong> + <strong>{i.medication_b_name}</strong>
                  <span className="text-muted"> — {i.description}</span>
                </li>
              ))}
            </ul>
            {interactionPreview.blocking ? (
              <p className="cp-interaction-panel__hint mb-0">{copy.INTERACTION_BLOCKING_HINT}</p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
